import { useCallback, useMemo, useState } from "react";
import { HighlightedMarkdown } from "~/components/HighlightedMarkdown";
import {
  buildTermMap,
  TermDefinitionDialog,
} from "~/components/TermDefinitionDialog";
import { Button } from "~/components/ui/button";
import { useMedications } from "~/features/chat/hooks/useMedications";
import { useMedicationsStore } from "~/features/chat/stores/medicationsStore";
import type { Hint } from "~/features/chat/types";
import { getMedicationMatcher, TermMatcher } from "~/utils/termMatcher";

const SAMPLE_AI_TERMS: Hint[] = [
  {
    term: "Hypertension",
    definition: "Persistently elevated arterial blood pressure.",
  },
  {
    term: "Diabetes Mellitus",
    definition: "A group of metabolic diseases characterized by hyperglycemia.",
  },
  {
    term: "Myocardial Infarction",
    definition: "Death of heart muscle due to prolonged ischemia.",
  },
  {
    term: "Atrial Fibrillation",
    definition: "An irregular and often rapid heart rhythm.",
  },
  {
    term: "Chronic Kidney Disease",
    definition: "Progressive loss of kidney function over time.",
  },
  {
    term: "Heart Failure",
    definition: "Condition where the heart can't pump blood efficiently.",
  },
  {
    term: "Pulmonary Embolism",
    definition: "A blockage in one of the pulmonary arteries in the lungs.",
  },
  {
    term: "Deep Vein Thrombosis",
    definition: "A blood clot in a deep vein, usually in the legs.",
  },
  {
    term: "Coronary Artery Disease",
    definition: "Disease of the major blood vessels that supply the heart.",
  },
  {
    term: "Congestive Heart Failure",
    definition: "Chronic progressive condition affecting heart pumping power.",
  },
];

const SAMPLE_CONTENT = `## Medical Report Section

The patient is a 72-year-old female with a longstanding history of Hypertension, Diabetes Mellitus type 2, and Coronary Artery Disease. She presented to the emergency department with acute onset chest pain radiating to the left arm, concerning for Myocardial Infarction.

### Past Medical History

- Hypertension — diagnosed 15 years ago, currently on triple therapy
- Diabetes Mellitus — HbA1c 7.8%, on Metformin and Glipizide
- Atrial Fibrillation — paroxysmal, rate-controlled with Metoprolol
- Chronic Kidney Disease stage 3b — baseline creatinine 1.8
- Heart Failure with preserved ejection fraction
- History of Deep Vein Thrombosis (2019) — completed Warfarin therapy
- Coronary Artery Disease — prior PCI with drug-eluting stent (2020)

### Current Medications

1. Metformin 1000mg BID — glucose control
2. Glipizide 10mg daily — adjunctive glucose control
3. Lisinopril 20mg daily — blood pressure and renal protection
4. Amlodipine 10mg daily — blood pressure
5. Metoprolol succinate 100mg daily — rate control for Atrial Fibrillation
6. Atorvastatin 80mg daily — lipid management post-MI
7. Aspirin 81mg daily — antiplatelet therapy
8. Clopidogrel 75mg daily — dual antiplatelet post-PCI
9. Furosemide 40mg BID — volume management
10. Spironolactone 25mg daily — Heart Failure management
11. Apixaban 5mg BID — anticoagulation for Atrial Fibrillation
12. Omeprazole 40mg daily — GI prophylaxis with dual antiplatelet
13. Insulin glargine 24 units at bedtime — basal insulin coverage
14. Albuterol inhaler PRN — reactive airway symptoms

### Assessment and Plan

Given the presentation consistent with Myocardial Infarction in the setting of known Coronary Artery Disease, cardiology was consulted. Troponin trended up to 2.4 ng/mL. ECG showed ST depression in leads V4-V6.

The patient's Chronic Kidney Disease complicates contrast use for catheterization. Her Diabetes Mellitus requires Metformin to be held 48 hours prior to any contrast procedure. The combination of Aspirin, Clopidogrel, and Apixaban raises significant bleeding risk.

Regarding her Atrial Fibrillation, the rate appears well-controlled on current Metoprolol dose. We will continue Apixaban for stroke prevention given her CHA2DS2-VASc score of 6.

Her Heart Failure appears euvolemic on current Furosemide and Spironolactone regimen. We will monitor closely for volume overload given IV fluids for renal protection.

No evidence of Pulmonary Embolism on CT angiogram. Lower extremity dopplers negative for Deep Vein Thrombosis.

The patient was taken to the catheterization lab where a 90% stenosis of the LAD was found and stented. She will require extended dual antiplatelet therapy. Congestive Heart Failure management will be optimized post-procedure.
`;

interface BenchResult {
  label: string;
  termsCount: number;
  contentLength: number;
  iterations: number;
  totalMs: number;
  avgMs: number;
  opsPerSec: number;
}

export const TermHighlightingBench = () => {
  const [results, setResults] = useState<BenchResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  useMedications();
  const medications = useMedicationsStore((s) => s.medications);
  const medicationMatcher = useMemo(
    () => getMedicationMatcher(medications),
    [medications]
  );
  const termMap = useMemo(
    () => buildTermMap(SAMPLE_AI_TERMS, medications),
    [medications]
  );

  const runBenchmark = useCallback(async () => {
    setIsRunning(true);
    setResults([]);

    const benchResults: BenchResult[] = [];
    const iterations = 100;

    // Multiplied content for stress testing
    const largeContent = Array(5).fill(SAMPLE_CONTENT).join("\n\n---\n\n");

    // Benchmark 1: AI terms only (regex), small content
    {
      // Direct regex benchmark
      const terms = SAMPLE_AI_TERMS;
      const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
      const pattern = sorted
        .map((t) => t.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
      const regex = new RegExp(`\\b(${pattern})\\b`, "gi");

      const start = performance.now();
      for (let i = 0; i < iterations; i++) {
        regex.lastIndex = 0;
        SAMPLE_CONTENT.replace(regex, (matched) => `<mark>${matched}</mark>`);
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "AI terms (regex) — normal content",
        termsCount: terms.length,
        contentLength: SAMPLE_CONTENT.length,
        iterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / iterations) * 1000) / 1000,
        opsPerSec: Math.round(iterations / (elapsed / 1000)),
      });
    }

    // Benchmark 2: AI terms only (regex), large content
    {
      const terms = SAMPLE_AI_TERMS;
      const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
      const pattern = sorted
        .map((t) => t.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
      const regex = new RegExp(`\\b(${pattern})\\b`, "gi");

      const start = performance.now();
      for (let i = 0; i < iterations; i++) {
        regex.lastIndex = 0;
        largeContent.replace(regex, (matched) => `<mark>${matched}</mark>`);
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "AI terms (regex) — 5x content",
        termsCount: terms.length,
        contentLength: largeContent.length,
        iterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / iterations) * 1000) / 1000,
        opsPerSec: Math.round(iterations / (elapsed / 1000)),
      });
    }

    // Benchmark 3: Medications (Aho-Corasick), normal content
    if (medications.length > 0) {
      const matcher = new TermMatcher(medications.map((m) => m.term));
      const start = performance.now();
      for (let i = 0; i < iterations; i++) {
        matcher.search(SAMPLE_CONTENT);
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "Medications (Aho-Corasick) — normal content",
        termsCount: medications.length,
        contentLength: SAMPLE_CONTENT.length,
        iterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / iterations) * 1000) / 1000,
        opsPerSec: Math.round(iterations / (elapsed / 1000)),
      });
    }

    // Benchmark 4: Medications (Aho-Corasick), large content
    if (medications.length > 0) {
      const matcher = new TermMatcher(medications.map((m) => m.term));
      const start = performance.now();
      for (let i = 0; i < iterations; i++) {
        matcher.search(largeContent);
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "Medications (Aho-Corasick) — 5x content",
        termsCount: medications.length,
        contentLength: largeContent.length,
        iterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / iterations) * 1000) / 1000,
        opsPerSec: Math.round(iterations / (elapsed / 1000)),
      });
    }

    // Benchmark 5: Medications via naive regex (comparison baseline)
    if (medications.length > 0) {
      const medTerms = medications.slice(0, 100); // Limit to first 100 for regex
      const sorted = [...medTerms].sort(
        (a, b) => b.term.length - a.term.length
      );
      const pattern = sorted
        .map((t) => t.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
      const regex = new RegExp(`\\b(${pattern})\\b`, "gi");

      const start = performance.now();
      for (let i = 0; i < iterations; i++) {
        regex.lastIndex = 0;
        SAMPLE_CONTENT.replace(regex, (matched) => `<mark>${matched}</mark>`);
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "Medications (regex, 100 terms) — normal content",
        termsCount: 100,
        contentLength: SAMPLE_CONTENT.length,
        iterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / iterations) * 1000) / 1000,
        opsPerSec: Math.round(iterations / (elapsed / 1000)),
      });
    }

    // Benchmark 6: Automaton build time
    if (medications.length > 0) {
      const buildIterations = 10;
      const start = performance.now();
      for (let i = 0; i < buildIterations; i++) {
        new TermMatcher(medications.map((m) => m.term));
      }
      const elapsed = performance.now() - start;
      benchResults.push({
        label: "Automaton build time (cold)",
        termsCount: medications.length,
        contentLength: 0,
        iterations: buildIterations,
        totalMs: Math.round(elapsed * 100) / 100,
        avgMs: Math.round((elapsed / buildIterations) * 1000) / 1000,
        opsPerSec: Math.round(buildIterations / (elapsed / 1000)),
      });
    }

    setResults(benchResults);
    setIsRunning(false);
  }, [medications]);

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Term Highlighting Benchmark</h1>
        <p className="text-muted-foreground mt-1">
          Performance evaluation: regex (AI terms) vs Aho-Corasick (medications)
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Medications loaded: {medications.length} terms
        </p>
      </div>

      <div className="flex gap-3">
        <Button onClick={runBenchmark} disabled={isRunning}>
          {isRunning ? "Running..." : "Run Benchmark"}
        </Button>
        <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? "Hide Preview" : "Show Highlight Preview"}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-3">Benchmark</th>
                <th className="text-right p-3">Terms</th>
                <th className="text-right p-3">Content</th>
                <th className="text-right p-3">Avg (ms)</th>
                <th className="text-right p-3">Ops/sec</th>
                <th className="text-right p-3">Total (ms)</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="p-3 font-medium">{r.label}</td>
                  <td className="text-right p-3">
                    {r.termsCount.toLocaleString()}
                  </td>
                  <td className="text-right p-3">
                    {r.contentLength > 0
                      ? `${(r.contentLength / 1024).toFixed(1)}KB`
                      : "—"}
                  </td>
                  <td className="text-right p-3 font-mono">{r.avgMs}</td>
                  <td className="text-right p-3 font-mono">
                    {r.opsPerSec.toLocaleString()}
                  </td>
                  <td className="text-right p-3 font-mono text-muted-foreground">
                    {r.totalMs}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPreview && (
        <div
          ref={setContainerEl}
          className="border rounded-lg p-4 prose prose-sm max-w-none"
        >
          <TermDefinitionDialog containerRef={containerEl} termMap={termMap} />
          <HighlightedMarkdown
            terms={SAMPLE_AI_TERMS}
            medicationMatcher={medicationMatcher}
          >
            {SAMPLE_CONTENT}
          </HighlightedMarkdown>
        </div>
      )}
    </div>
  );
};
